import { useCallback, useEffect, useState } from "react";

import { useAppDispatch } from "store/hooks";
import { handleGetDealerDriversRequest } from "store/modules/DealerUsers/thunk.store";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";

import {
  clearDealerDriversInflight,
  clearDealerDriversMemory,
  getDealerDriversFromMemory,
  getDealerDriversInflight,
  hasDealerDriversInMemory,
  setDealerDriversInMemory,
  setDealerDriversInflight,
} from "./dealerDriversMemory";

const DEALER_DRIVERS_PAGE = { limit: 200 } as const;

export function useProductsAndCoverageDealerDrivers(dealerId: string) {
  const dispatch = useAppDispatch();
  const [dealerDrivers, setDealerDrivers] = useState<DealerUsersProps[]>(
    () => getDealerDriversFromMemory(dealerId) ?? [],
  );
  const [isDealerDriversLoaded, setIsDealerDriversLoaded] = useState(() =>
    hasDealerDriversInMemory(dealerId),
  );

  useEffect(() => {
    const cached = getDealerDriversFromMemory(dealerId);
    setDealerDrivers(cached ?? []);
    setIsDealerDriversLoaded(hasDealerDriversInMemory(dealerId));
  }, [dealerId]);

  useEffect(() => clearDealerDriversMemory, []);

  const ensureDealerDriversLoaded = useCallback(async (): Promise<
    DealerUsersProps[]
  > => {
    if (!dealerId) {
      return [];
    }

    const cached = getDealerDriversFromMemory(dealerId);
    if (cached) {
      setDealerDrivers(cached);
      setIsDealerDriversLoaded(true);
      return cached;
    }

    const inflight = getDealerDriversInflight(dealerId);
    if (inflight) {
      const data = await inflight;
      setDealerDrivers(data);
      setIsDealerDriversLoaded(true);
      return data;
    }

    const promise = dispatch(
      handleGetDealerDriversRequest({
        dealerId,
        ...DEALER_DRIVERS_PAGE,
        suppressGlobalSpinner: true,
      }),
    )
      .unwrap()
      .then((response) => {
        const data = response?.data ?? [];
        setDealerDriversInMemory(dealerId, data);
        setDealerDrivers(data);
        setIsDealerDriversLoaded(true);
        return data;
      })
      .finally(() => {
        clearDealerDriversInflight(dealerId);
      });

    setDealerDriversInflight(dealerId, promise);
    return promise;
  }, [dealerId, dispatch]);

  return {
    dealerDrivers,
    ensureDealerDriversLoaded,
    isDealerDriversLoaded,
  };
}
